import React, { useState } from 'react';
import { Modal } from './ui/Modal';
import { LoanFile, FileNote } from '../types/goals';
import { Badge } from './ui/Badge';
import { ExternalLink, Clock, DollarSign, MapPin, User, FileText, MessageSquare } from 'lucide-react';
import { Button } from './ui/Button';
import { FileNotesModal } from './FileNotesModal';
interface FileListModalProps {
  isOpen: boolean;
  onClose: () => void;
  stage: 'awc' | 'appraisalBack';
  files: LoanFile[];
  processorName?: string;
  userRole?: 'manager' | 'processor'; // Add role prop
}
// Mock notes generator for individual files
const generateFileNotes = (file: LoanFile): FileNote[] => {
  const noteTemplates = [`Please follow up on the missing income documents for ${file.borrowerName}.`, `Great progress! ${file.borrowerName} file is moving smoothly.`, `Need to expedite appraisal for ${file.borrowerName} - closing date approaching.`, `Underwriter requested additional employment verification.`, `Title issues resolved. Ready to move forward.`, `Borrower is responsive and providing docs quickly.`, `HOA documents received and reviewed.`];
  const urgentTemplates = [`URGENT: ${file.borrowerName} closing in 48 hours - need final conditions cleared.`, `Critical: Appraisal came in low. Need to discuss options with borrower ASAP.`, `URGENT: Missing insurance binder - closing cannot proceed without it.`];
  const now = new Date();
  const notes: FileNote[] = [];
  // Generate 2-4 notes per file
  const noteCount = Math.floor(Math.random() * 3) + 2;
  for (let i = 0; i < noteCount; i++) {
    const isUrgent = Math.random() < 0.15; // 15% chance of urgent
    const templates = isUrgent ? urgentTemplates : noteTemplates;
    const hoursAgo = Math.floor(Math.random() * 96); // Last 4 days
    notes.push({
      id: `note-${file.id}-${i}`,
      fileId: file.id,
      borrowerName: file.borrowerName,
      author: i % 3 === 0 ? file.loanOfficer : 'Manager',
      authorRole: i % 3 === 0 ? 'processor' : 'manager',
      content: templates[Math.floor(Math.random() * templates.length)],
      timestamp: new Date(now.getTime() - hoursAgo * 3600000),
      isUrgent
    });
  }
  return notes;
};
export function FileListModal({
  isOpen,
  onClose,
  stage,
  files,
  processorName,
  userRole = 'manager' // Default to manager for backward compatibility
}: FileListModalProps) {
  const [notesModalState, setNotesModalState] = useState<{
    isOpen: boolean;
    file: LoanFile | null;
    notes: FileNote[];
  }>({
    isOpen: false,
    file: null,
    notes: []
  });
  const stageConfig = {
    awc: {
      title: processorName ? `${processorName} - AWC Files` : 'AWC Files',
      description: userRole === 'manager' ? 'Active Work Condition - Files requiring immediate attention. Click a file to view notes.' : 'Active Work Condition - Files requiring immediate attention. Click a file to view or leave notes.',
      emptyMessage: 'No files in AWC stage'
    },
    appraisalBack: {
      title: processorName ? `${processorName} - Appraisal Back Files` : 'Appraisal Back Files',
      description: userRole === 'manager' ? 'Files with appraisals returned - Monitor for issues. Click a file to view notes.' : 'Files with appraisals returned - Monitor for issues. Click a file to view or leave notes.',
      emptyMessage: 'No files with appraisals back'
    }
  };
  const config = stageConfig[stage];
  const getPriorityBadge = (priority?: 'high' | 'medium' | 'low') => {
    if (!priority) return null;
    const variants = {
      high: 'danger' as const,
      medium: 'warning' as const,
      low: 'neutral' as const
    };
    return <Badge variant={variants[priority]} data-id="element-13">{priority.toUpperCase()}</Badge>;
  };
  const handleFileClick = (file: LoanFile) => {
    const notes = generateFileNotes(file);
    setNotesModalState({
      isOpen: true,
      file,
      notes
    });
  };
  const handleAddNote = (content: string, isUrgent: boolean) => {
    if (!notesModalState.file) return;
    const newNote: FileNote = {
      id: `note-${Date.now()}`,
      fileId: notesModalState.file.id,
      borrowerName: notesModalState.file.borrowerName,
      author: userRole === 'manager' ? 'Manager' : notesModalState.file.loanOfficer,
      authorRole: userRole,
      content,
      timestamp: new Date(),
      isUrgent
    };
    setNotesModalState({
      ...notesModalState,
      notes: [newNote, ...notesModalState.notes]
    });
    console.log('New note added:', newNote);
  };
  const closeNotesModal = () => {
    setNotesModalState({
      isOpen: false,
      file: null,
      notes: []
    });
  };
  return <>
      <Modal isOpen={isOpen} onClose={onClose} title={config.title} description={config.description} size="xl" footer={<>
            <Button variant="outline" onClick={onClose} data-id="element-15">
              Close
            </Button>
            <Button variant="primary" data-id="element-16">
              <ExternalLink className="w-4 h-4 mr-2" data-id="element-17" />
              Open in Salesforce
            </Button>
          </>} data-id="element-14">
        {files.length === 0 ? <div className="text-center py-8 text-slate-500" data-id="element-18">
            {config.emptyMessage}
          </div> : <div className="space-y-3" data-id="element-19">
            {files.map(file => <div key={file.id} onClick={() => handleFileClick(file)} className="border border-slate-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer group" data-id="element-20">
                <div className="flex items-start justify-between mb-3" data-id="element-21">
                  <div className="flex-1" data-id="element-22">
                    <div className="flex items-center gap-2 mb-1" data-id="element-23">
                      <h4 className="font-semibold text-slate-900 group-hover:text-blue-700 transition-colors" data-id="element-24">
                        {file.borrowerName}
                      </h4>
                      {getPriorityBadge(file.priority)}
                      <MessageSquare className="w-4 h-4 text-slate-400 group-hover:text-blue-600 ml-auto" data-id="element-25" />
                    </div>
                    <p className="text-sm text-slate-500" data-id="element-26">{file.status}</p>
                  </div>
                  <Badge variant="neutral" className="flex items-center gap-1" data-id="element-27">
                    <Clock className="w-3 h-3" data-id="element-28" />
                    {file.daysInStage} days
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm" data-id="element-29">
                  <div className="flex items-center gap-2 text-slate-600" data-id="element-30">
                    <DollarSign className="w-4 h-4 text-slate-400" data-id="element-31" />
                    <span data-id="element-32">${file.loanAmount.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600" data-id="element-33">
                    <MapPin className="w-4 h-4 text-slate-400" data-id="element-34" />
                    <span className="truncate" data-id="element-35">{file.propertyAddress}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600" data-id="element-36">
                    <User className="w-4 h-4 text-slate-400" data-id="element-37" />
                    <span data-id="element-38">{file.loanOfficer}</span>
                  </div>
                  {file.openConditions !== undefined && <div className="flex items-center gap-2 text-slate-600" data-id="element-39">
                      <FileText className="w-4 h-4 text-slate-400" data-id="element-40" />
                      <span className="font-medium" data-id="element-41">
                        {file.openConditions}{' '}
                        {file.openConditions === 1 ? 'condition' : 'conditions'}
                      </span>
                    </div>}
                </div>
              </div>)}
          </div>}
      </Modal>

      <FileNotesModal isOpen={notesModalState.isOpen} onClose={closeNotesModal} file={notesModalState.file} notes={notesModalState.notes} onAddNote={handleAddNote} userRole={userRole} // Pass userRole to FileNotesModal
    data-id="element-42" />
    </>;
}